import {
  Typography,
  Container,
  Box,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Tab,
  Tabs,
  Skeleton,
} from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import axios, { pluckData } from 'src/utilities/http-client'
import { useEffect, useState } from 'react'
import { BarChart, LineChart, PieChart } from '@mui/x-charts'
import { nullSafeValueFormatter } from 'src/v3/utilities/charts'

type GeneralStats = {
  totalSubmissions: number
  totalCost: number
  successRate: number
  averageDailySubmissions: number
}

type LicenseLines = {
  emsTotals: number
  nurseTotals: number
  otherTotals: number
  labels: string[]
}

type EMSBreakdown = {
  licenseType: string
  total: number
}[]

type CostTrend = {
  costs: number
  labels: string[]
}

type CostTrendByLicenseType = {
  labels: string[]
  series: {
    label: string
    data: number[]
  }[]
}

type LicenseGroupDistribution = {
  id: string
  value: number
  label: string
}[]

export default () => {
  let [dateLowerFilter, setDateLowerFilter] = useState<any>(
    dayjs().subtract(1, 'month').format(),
  )
  let [dateUpperFilter, setDateUpperFilter] = useState<any>(dayjs())
  let [generalStats, setGeneralStats] = useState<GeneralStats>()
  let [licenseLines, setLicenseLines] = useState<LicenseLines>()
  let [emsBreakdown, setEMSBreakdown] = useState<EMSBreakdown>()
  let [costTrend, setCostTrend] = useState<CostTrend>()
  let [groupData, setGroupData] = useState<LicenseGroupDistribution>([
    { id: 'ems', value: 0, label: 'EMS' },
    { id: 'nurse', value: 0, label: 'Nurse' },
    { id: 'other', value: 0, label: 'Other' },
  ])
  let [costTrendByLicenseType, setCostTrendByLicenseType] =
    useState<CostTrendByLicenseType>()
  let [chartTab, setChartTab] = useState('overview')
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [loading, setLoading] = useState(true)

  let getGeneralStats = () =>
    axios
      .get<GeneralStats>(
        `/api/scribe/capce-submission-report/general-stats/${dayjs(dateLowerFilter).format('MM-DD-YYYY')}/${dayjs(dateUpperFilter).format('MM-DD-YYYY')}`,
      )
      .then(pluckData)
      .then((res) => {
        setGeneralStats(res)
      })
      .catch((err) =>
        setErrorMessage(
          `General stats for CAPCE failed to load: ${err.response?.data || err}`,
        ),
      )

  let getLicenseLines = () =>
    axios
      .get<LicenseLines>(
        `/api/scribe/capce-submission-report/license-lines/${dayjs(dateLowerFilter).format('MM-DD-YYYY')}/${dayjs(dateUpperFilter).format('MM-DD-YYYY')}`,
      )
      .then(pluckData)
      .then((res) => {
        setLicenseLines(res)
      })
      .catch((err) =>
        setErrorMessage(
          `License type trends for CAPCE failed to load: ${err.response?.data || err}`,
        ),
      )

  let getEMSBreakdown = () =>
    axios
      .get<EMSBreakdown>(
        `/api/scribe/capce-submission-report/ems-breakdown/${dayjs(dateLowerFilter).format('MM-DD-YYYY')}/${dayjs(dateUpperFilter).format('MM-DD-YYYY')}`,
      )
      .then(pluckData)
      .then((res) => {
        setEMSBreakdown(res)
      })
      .catch((err) =>
        setErrorMessage(
          `EMS Breakdown for CAPCE failed to load: ${err.response?.data || err}`,
        ),
      )

  let getCostTrend = () =>
    axios
      .get<CostTrend>(
        `/api/scribe/capce-submission-report/cost-trend/${dayjs(dateLowerFilter).format('MM-DD-YYYY')}/${dayjs(dateUpperFilter).format('MM-DD-YYYY')}`,
      )
      .then(pluckData)
      .then((res) => {
        setCostTrend(res)
      })
      .catch((err) =>
        setErrorMessage(
          `EMS Breakdown for CAPCE failed to load: ${err.response?.data || err}`,
        ),
      )

  let getGroupData = () =>
    axios
      .get<LicenseGroupDistribution>(
        `/api/scribe/capce-submission-report/license-distribution/${dayjs(dateLowerFilter).format('MM-DD-YYYY')}/${dayjs(dateUpperFilter).format('MM-DD-YYYY')}`,
      )
      .then(pluckData)
      .then((res) => {
        setGroupData(res)
      })
      .catch((err) =>
        setErrorMessage(
          `License group distribution for CAPCE failed to load: ${err.response?.data || err}`,
        ),
      )

  let getCostTrendByLicenseType = () =>
    axios
      .get<CostTrendByLicenseType>(
        `/api/scribe/capce-submission-report/cost-trend-by-license/${dayjs(dateLowerFilter).format('MM-DD-YYYY')}/${dayjs(dateUpperFilter).format('MM-DD-YYYY')}`,
      )
      .then(pluckData)
      .then((res) => {
        setCostTrendByLicenseType(res)
      })
      .catch((err) =>
        setErrorMessage(
          `Cost trend by license type for CAPCE failed to load: ${err.response?.data || err}`,
        ),
      )

  useEffect(() => {
    if (dateLowerFilter && dateUpperFilter) {
      setLoading(true)
      Promise.all([
        getGeneralStats(),
        getLicenseLines(),
        getEMSBreakdown(),
        getCostTrend(),
        getCostTrendByLicenseType(),
        getGroupData(),
      ])
        .then(() => setLoading(false))
        .catch((err) => {
          setErrorMessage(
            `CAPCE submission report failed to load: ${err.response?.data || err}`,
          )
        })
    }
  }, [dateLowerFilter, dateUpperFilter])

  return (
    <>
      <Container maxWidth="xl">
        {!loading ? (
          <>
            <Box className="grid grid-cols-6">
              <Box className="col-span-6 2xl:col-span-3">
                <Box>
                  <Typography
                    variant="h1"
                    sx={{ mb: 0, lineHeight: 'inherit' }}
                  >
                    CAPCE Submission Report
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    *No data available before 4/24/2025
                  </Typography>
                </Box>
              </Box>
              <Box
                className="col-span-6 2xl:col-span-3"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  mb: 2,
                  mt: 3,
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{ width: 160 }}
                    label="From"
                    value={dayjs(dateLowerFilter)}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(newValue) => setDateLowerFilter(newValue)}
                  />
                  <DatePicker
                    sx={{ width: 160, ml: 2 }}
                    label="To"
                    value={dayjs(dateUpperFilter)}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(newValue) => setDateUpperFilter(newValue)}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
            <Box className="grid grid-cols-12 gap-4 mt-4">
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card>
                  <CardContent>
                    <Typography variant="h5">Total Submissions</Typography>
                    <Typography variant="h2">
                      {generalStats?.totalSubmissions}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card>
                  <CardContent>
                    <Typography variant="h5">Total Cost</Typography>
                    <Typography variant="h2">
                      ${generalStats?.totalCost}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card>
                  <CardContent>
                    <Typography variant="h5">Success Rate</Typography>
                    <Typography variant="h2">
                      {generalStats?.successRate}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card>
                  <CardContent>
                    <Typography variant="h5">Average Daily</Typography>
                    <Typography variant="h2">
                      {generalStats?.averageDailySubmissions}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12">
                <Tabs
                  variant="scrollable"
                  value={chartTab}
                  onChange={(e, val) => setChartTab(val)}
                >
                  <Tab value="overview" label="Overview" />
                  <Tab value="emsBreakdown" label="EMS Breakdown" />
                  <Tab value="costAnalysis" label="Cost Analysis" />
                  <Tab value="costByLicense" label="Cost by License Type" />
                </Tabs>
                {chartTab === 'overview' && (
                  <LineChart
                    height={500}
                    series={[
                      {
                        data: licenseLines?.emsTotals ?? [],
                        label: 'EMS',
                        valueFormatter: nullSafeValueFormatter,
                      } as any,
                      {
                        data: licenseLines?.nurseTotals ?? [],
                        label: 'Nurse',
                        valueFormatter: nullSafeValueFormatter,
                      } as any,
                      {
                        data: licenseLines?.otherTotals ?? [],
                        label: 'Other',
                        valueFormatter: nullSafeValueFormatter,
                      } as any,
                    ]}
                    xAxis={[
                      { scaleType: 'point', data: licenseLines?.labels ?? [] },
                    ]}
                    yAxis={[{ width: 50 } as any]}
                  />
                )}
                {chartTab === 'emsBreakdown' && (
                  <BarChart
                    height={400}
                    margin={{ left: 100 }}
                    dataset={emsBreakdown ?? []}
                    yAxis={[{ scaleType: 'band', dataKey: 'licenseType' }]}
                    series={[
                      {
                        dataKey: 'total',
                        label: 'Submissions',
                      },
                    ]}
                    layout="horizontal"
                    xAxis={[{ label: 'Submission Count' }]}
                  />
                )}
                {chartTab === 'costAnalysis' && (
                  <LineChart
                    height={500}
                    series={[
                      {
                        data: costTrend?.costs ?? [],
                        label: 'Cost',
                        valueFormatter: (value: any) =>
                          value == null
                            ? ''
                            : `$${(value as number).toFixed(2)}`,
                      } as any,
                    ]}
                    xAxis={[
                      { scaleType: 'point', data: costTrend?.labels ?? [] },
                    ]}
                    yAxis={[{ width: 50 } as any]}
                  />
                )}
                {chartTab === 'costByLicense' && (
                  <LineChart
                    margin={{ right: 24, left: 60 }}
                    height={500}
                    series={
                      costTrendByLicenseType?.series.map(({ label, data }) => ({
                        label,
                        data,
                        area: true,
                        stack: 'total',
                        showMark: false,
                        valueFormatter: (value: any) =>
                          `$${value?.toFixed(2) ?? '$0.00'}`,
                      })) ?? []
                    }
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: costTrendByLicenseType?.labels ?? [],
                      },
                    ]}
                    yAxis={[
                      {
                        valueFormatter: (value: any) => `$${value.toFixed(2)}`,
                      },
                    ]}
                  />
                )}
              </Box>
              <Box className="col-span-12">
                <Card>
                  <CardContent>
                    <Typography variant="h6">
                      License Group Distribution
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                      }}
                    >
                      <PieChart
                        height={500}
                        width={500}
                        series={[{ data: groupData }]}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </>
        ) : (
          // make Skeletons for the whole layout
          <Box className="grid grid-cols-12 gap-4 mt-4">
            <Box className="col-span-6 2xl:col-span-3">
              <Box>
                <Typography variant="h1" sx={{ mb: 0, lineHeight: 'inherit' }}>
                  <Skeleton />
                </Typography>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  <Skeleton />
                </Typography>
              </Box>
            </Box>
            <Box
              className="col-span-6 2xl:col-span-3"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mb: 2,
                mt: 3,
              }}
            >
              <Skeleton
                sx={{
                  width: 160,
                  height: 40,
                }}
              />
              <Skeleton
                sx={{
                  width: 160,
                  height: 40,
                }}
              />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton
                sx={{
                  height: 136,
                }}
              />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton
                sx={{
                  height: 136,
                }}
              />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton
                sx={{
                  height: 136,
                }}
              />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton
                sx={{
                  height: 136,
                }}
              />
            </Box>
            <Box className="col-span-12">
              <Skeleton
                sx={{
                  height: 548,
                }}
              />
            </Box>
            <Box className="col-span-12">
              <Skeleton
                sx={{
                  height: 548,
                }}
              />
            </Box>
          </Box>
        )}
      </Container>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
